
import React, { useEffect, useState, useRef } from 'react';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
const WS_BASE = (import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com')
  .replace('https://', 'wss://')
  .replace('http://', 'ws://');

export default function TokenWaitPage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [status, setStatus] = useState<'waiting' | 'ready' | 'collected'>('waiting');
  const [waitingScene, setWaitingScene] = useState<'chef' | 'shop'>('chef');
  const [sceneKey, setSceneKey] = useState(0); // bump to restart the fade-in animation each rotation

  useEffect(() => {
    const interval = setInterval(() => {
      setWaitingScene(prev => prev === 'chef' ? 'shop' : 'chef');
      setSceneKey(k => k + 1);
    }, 6000); // swap scene every 6s while waiting
    return () => clearInterval(interval);
  }, []);
  const [orderInfo, setOrderInfo] = useState<any>(null);
  const [connected, setConnected] = useState(false);
  const audioRef = useRef<AudioContext | null>(null);
  const alarmRef = useRef<any>(null);

  // ── Fetch initial status ──────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND}/tokens/${token}`)
      .then(r => r.json())
      .then(data => {
        setOrderInfo(data);
        setStatus(data.status);
      })
      .catch(() => {});
  }, [token]);

  // ── WebSocket connection, with auto-reconnect ──────────────────────────────
  // Previously: one disconnect (wifi blip, screen lock, backend restart) and
  // onclose just gave up silently — connected flipped to false and nothing
  // ever retried, so you'd be stuck on stale status until a manual refresh.
  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);

  useEffect(() => {
    if (!token) return;
    let ws: WebSocket;
    let reconnectTimer: ReturnType<typeof setTimeout>;
    let closedByUs = false;

    const connect = () => {
      ws = new WebSocket(`${WS_BASE}/ws/token/${token}`);
      ws.onopen = () => setConnected(true);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.status && data.status !== statusRef.current) {
            setStatus(data.status);
            if (data.status === 'ready') playAlarm();
          }
        } catch {}
      };
      ws.onclose = () => {
        setConnected(false);
        if (!closedByUs) reconnectTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => ws.close();
    };
    connect();

    return () => {
      closedByUs = true;
      clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [token]);

  // ── Polling fallback ─────────────────────────────────────────────────────
  // Belt-and-suspenders: even with reconnect, catches any status change the
  // socket missed (message dropped during the reconnect gap, etc.) within
  // a few seconds either way, instead of leaving you stuck.
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      fetch(`${BACKEND}/tokens/${token}`)
        .then(r => r.json())
        .then(data => {
          setOrderInfo(data);
          if (data.status && data.status !== statusRef.current) {
            const justBecameReady = data.status === 'ready' && statusRef.current !== 'ready';
            setStatus(data.status);
            if (justBecameReady) playAlarm();
          }
        })
        .catch(() => {});
    }, 4000);
    return () => clearInterval(interval);
  }, [token]);

  // ── Alarm sound ───────────────────────────────────────────────────────────
  const playAlarm = () => {
    try {
      const ctx = new AudioContext();
      audioRef.current = ctx;
      let time = ctx.currentTime;
      for (let i = 0; i < 6; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = i % 2 === 0 ? 880 : 660;
        osc.type = 'square';
        gain.gain.setValueAtTime(0.4, time);
        gain.gain.exponentialRampToValueAtTime(0.001, time + 0.3);
        osc.start(time);
        osc.stop(time + 0.3);
        time += 0.35;
      }
      alarmRef.current = ctx;
    } catch (e) {}

    // Also vibrate if supported
    if ('vibrate' in navigator) {
      navigator.vibrate([400, 100, 400, 100, 400]);
    }
  };

  if (!token) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ fontSize: 48 }}>❌</div>
          <h2 style={styles.title}>Invalid Link</h2>
          <p style={styles.sub}>No token found in URL.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, background: status === 'ready' ? '#f0fdf4' : '#f8fafc' }}>
      <div style={styles.card}>
        {/* Token */}
        <div style={styles.tokenBadge}>{token}</div>
        <p style={styles.tokenLabel}>Your Order Token</p>

        {/* Status */}
        {status === 'waiting' && waitingScene === 'chef' && (
          <React.Fragment key={sceneKey}>
            <svg viewBox="0 0 200 160" width="200" height="160" style={{ margin: '4px auto 8px' }} className="sceneFade">
              {/* Steam */}
              <path className="steam steam1" d="M 88 55 Q 82 45 88 38 Q 94 31 88 22" fill="none" stroke="#cbd5e1" strokeWidth="4" strokeLinecap="round" />
              <path className="steam steam2" d="M 100 55 Q 94 43 100 35 Q 106 27 100 18" fill="none" stroke="#cbd5e1" strokeWidth="4" strokeLinecap="round" />
              <path className="steam steam3" d="M 112 55 Q 106 45 112 38 Q 118 31 112 22" fill="none" stroke="#cbd5e1" strokeWidth="4" strokeLinecap="round" />

              {/* Pot */}
              <ellipse cx="100" cy="60" rx="34" ry="8" fill="#6366f1" opacity="0.15" />
              <path d="M 70 60 L 76 88 Q 78 94 84 94 L 116 94 Q 122 94 124 88 L 130 60 Z" fill="#818cf8" />
              <ellipse cx="100" cy="60" rx="30" ry="7" fill="#a5b4fc" />
              <rect x="58" y="56" width="14" height="6" rx="3" fill="#6366f1" />
              <rect x="128" y="56" width="14" height="6" rx="3" fill="#6366f1" />

              {/* Flame under pot */}
              <path className="flame" d="M 96 118 Q 92 110 96 104 Q 100 110 100 104 Q 104 110 100 118 Q 104 114 104 118 Q 100 124 96 120 Q 92 124 96 118 Z" fill="#f97316" />
              <path className="flame flameInner" d="M 98 116 Q 96 111 98 107 Q 101 112 100 116 Z" fill="#fbbf24" />
              <rect x="82" y="98" width="36" height="6" rx="3" fill="#94a3b8" />
              <rect x="86" y="104" width="28" height="4" rx="2" fill="#cbd5e1" />

              {/* Chef */}
              <g className="chefArm">
                <ellipse cx="140" cy="120" rx="14" ry="16" fill="#fde68a" />
                <path d="M 132 108 Q 124 100 130 92 Q 138 86 148 92 Q 154 98 148 106 Z" fill="#fff" />
                <circle cx="140" cy="96" r="3" fill="#1e293b" />
                <path d="M 100 90 L 138 100" stroke="#fde68a" strokeWidth="7" strokeLinecap="round" />
              </g>
              <ellipse cx="140" cy="140" rx="20" ry="10" fill="#eef2ff" />
            </svg>
            <h2 style={{ ...styles.title, color: '#1e293b' }} className="sceneFade">Your order is being prepared...</h2>
            <p style={styles.sub}>Please stay nearby. We'll alert you here when it's ready!</p>
          </React.Fragment>
        )}

        {status === 'waiting' && waitingScene === 'shop' && (
          <React.Fragment key={sceneKey}>
            <svg viewBox="0 0 200 160" width="200" height="160" style={{ margin: '4px auto 8px' }} className="sceneFade">
              {/* Shelves in background */}
              <rect x="14" y="18" width="172" height="6" rx="2" fill="#e2e8f0" />
              <rect x="14" y="40" width="172" height="6" rx="2" fill="#e2e8f0" />
              {[18, 34, 50, 66, 150, 166].map((x, i) => (
                <rect key={i} x={x} y={22} width="10" height="14" rx="2"
                  fill={['#fbbf24','#4ade80','#f472b6','#818cf8','#fb923c','#38bdf8'][i]} />
              ))}
              {[18, 34, 50, 150, 166].map((x, i) => (
                <rect key={`b${i}`} x={x} y={44} width="10" height="12" rx="2"
                  fill={['#a78bfa','#facc15','#34d399','#f87171','#60a5fa'][i]} />
              ))}

              {/* Counter */}
              <rect x="30" y="108" width="140" height="10" rx="3" fill="#cbd5e1" />
              <rect x="34" y="118" width="132" height="20" rx="2" fill="#e2e8f0" />

              {/* Checklist */}
              <g className="shopList">
                <rect x="40" y="92" width="30" height="24" rx="2" fill="#fff" stroke="#cbd5e1" strokeWidth="1.5" />
                <line x1="45" y1="98" x2="65" y2="98" stroke="#cbd5e1" strokeWidth="1.5" />
                <line x1="45" y1="103" x2="65" y2="103" stroke="#cbd5e1" strokeWidth="1.5" />
                <path className="checkMark" d="M 46 108 L 50 112 L 58 103" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </g>

              {/* Shopkeeper packing a bag */}
              <circle cx="120" cy="70" r="12" fill="#fde68a" />
              <path d="M 105 86 Q 120 78 135 86 L 137 108 L 103 108 Z" fill="#22c55e" />
              <rect x="112" y="94" width="26" height="28" rx="3" fill="#a16207" className="groceryBag" />
              <path d="M 116 94 Q 116 86 125 86 Q 134 86 134 94" fill="none" stroke="#a16207" strokeWidth="3" />
              <g className="packArm">
                <path d="M 105 96 L 90 88" stroke="#fde68a" strokeWidth="7" strokeLinecap="round" />
                <circle cx="88" cy="86" r="6" fill="#fde68a" />
              </g>
            </svg>
            <h2 style={{ ...styles.title, color: '#1e293b' }} className="sceneFade">Your order is being packed...</h2>
            <p style={styles.sub}>Please stay nearby. We'll alert you here when it's ready!</p>
          </React.Fragment>
        )}

        {status === 'ready' && (
          <>
            <svg viewBox="0 0 200 160" width="200" height="160" style={{ margin: '4px auto 8px' }} className="popIn">
              {/* Sound wave arcs */}
              <path className="wave waveL1" d="M 62 78 Q 50 78 50 66" fill="none" stroke="#4ade80" strokeWidth="4" strokeLinecap="round" />
              <path className="wave waveL2" d="M 54 84 Q 34 84 34 60" fill="none" stroke="#86efac" strokeWidth="4" strokeLinecap="round" />
              <path className="wave waveR1" d="M 138 78 Q 150 78 150 66" fill="none" stroke="#4ade80" strokeWidth="4" strokeLinecap="round" />
              <path className="wave waveR2" d="M 146 84 Q 166 84 166 60" fill="none" stroke="#86efac" strokeWidth="4" strokeLinecap="round" />

              {/* Bell */}
              <g className="bell">
                <path d="M 100 40 Q 76 40 74 72 Q 73 84 64 92 L 136 92 Q 127 84 126 72 Q 124 40 100 40 Z" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2" />
                <rect x="93" y="30" width="14" height="12" rx="4" fill="#f59e0b" />
                <circle cx="100" cy="30" r="6" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2" />
                <rect x="60" y="92" width="80" height="8" rx="4" fill="#f59e0b" />
                <circle cx="100" cy="108" r="7" fill="#f59e0b" />
              </g>
            </svg>
            <h2 style={{ ...styles.title, color: '#16a34a', fontSize: 28 }} className="popIn">ORDER READY!</h2>
            <p style={{ ...styles.sub, color: '#15803d', fontWeight: 700 }}>
              Please collect your order at the counter.
            </p>
            <button onClick={playAlarm} style={styles.ringBtn}>🔊 Ring Again</button>
          </>
        )}

        {status === 'collected' && (
          <>
            <svg viewBox="0 0 200 160" width="200" height="160" style={{ margin: '4px auto 8px' }}>
              {/* Confetti */}
              {[
                { x: 40, y: 30, c: '#f59e0b', d: '0s' },
                { x: 160, y: 35, c: '#6366f1', d: '0.15s' },
                { x: 55, y: 100, c: '#22c55e', d: '0.3s' },
                { x: 150, y: 95, c: '#ec4899', d: '0.1s' },
                { x: 100, y: 20, c: '#4ade80', d: '0.25s' },
                { x: 30, y: 70, c: '#818cf8', d: '0.4s' },
                { x: 170, y: 65, c: '#fbbf24', d: '0.05s' },
              ].map((p, i) => (
                <rect key={i} className="confetti" x={p.x} y={p.y} width="7" height="7" rx="1.5"
                  fill={p.c} style={{ animationDelay: p.d }} />
              ))}

              {/* Counter */}
              <rect x="40" y="112" width="120" height="10" rx="3" fill="#e2e8f0" />
              <rect x="46" y="122" width="108" height="18" rx="2" fill="#cbd5e1" />

              {/* Customer walking up + bag */}
              <g className="customer">
                <circle cx="100" cy="66" r="12" fill="#fde68a" />
                <path d="M 84 82 Q 100 74 116 82 L 118 112 L 82 112 Z" fill="#6366f1" />
                <rect x="88" y="90" width="24" height="26" rx="4" fill="#a5b4fc" stroke="#818cf8" strokeWidth="2" className="bag" />
                <path d="M 92 90 Q 92 80 100 80 Q 108 80 108 90" fill="none" stroke="#818cf8" strokeWidth="3" />
              </g>
            </svg>
            <h2 style={{ ...styles.title, color: '#6366f1' }}>Enjoy your meal!</h2>
            <p style={styles.sub}>Thank you for visiting us.</p>
          </>
        )}

        {/* Order info */}
        {orderInfo?.customer_name && (
          <div style={styles.infoBox}>
            <span style={styles.infoLabel}>Name</span>
            <span style={styles.infoVal}>{orderInfo.customer_name}</span>
          </div>
        )}
        {orderInfo?.order_note && (
          <div style={styles.infoBox}>
            <span style={styles.infoLabel}>Order</span>
            <span style={styles.infoVal}>{orderInfo.order_note}</span>
          </div>
        )}

        {/* Connection dot */}
        <div style={styles.connRow}>
          <div style={{ ...styles.connDot, background: connected ? '#22c55e' : '#94a3b8' }} />
          <span style={styles.connText}>{connected ? 'Live connected' : 'Connecting...'}</span>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes wave {
          0%, 100% { height: 10px; }
          50% { height: 40px; }
        }
        @keyframes steamRise {
          0%   { opacity: 0; transform: translateY(4px) scaleY(0.8); }
          30%  { opacity: 0.9; }
          100% { opacity: 0; transform: translateY(-14px) scaleY(1.1); }
        }
        @keyframes stir {
          0%, 100% { transform: rotate(-6deg); transform-origin: 100px 90px; }
          50%      { transform: rotate(8deg); transform-origin: 100px 90px; }
        }
        @keyframes flicker {
          0%, 100% { transform: scaleY(1) scaleX(1); }
          50%      { transform: scaleY(1.15) scaleX(0.9); }
        }
        .steam { animation: steamRise 2.2s ease-in-out infinite; }
        .steam1 { animation-delay: 0s; }
        .steam2 { animation-delay: 0.5s; }
        .steam3 { animation-delay: 1s; }
        .chefArm { animation: stir 1.1s ease-in-out infinite; transform-origin: 100px 90px; }
        .flame { animation: flicker 0.5s ease-in-out infinite; transform-origin: 100px 118px; }
        .flameInner { animation: flicker 0.4s ease-in-out infinite reverse; transform-origin: 98px 116px; }

        @keyframes popIn {
          0%   { opacity: 0; transform: scale(0.5); }
          60%  { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes ringSwing {
          0%, 100% { transform: rotate(-8deg); }
          50%      { transform: rotate(8deg); }
        }
        @keyframes waveOut {
          0%   { opacity: 0; transform: scale(0.7); }
          40%  { opacity: 1; }
          100% { opacity: 0; transform: scale(1.15); }
        }
        @keyframes walkIn {
          0%   { opacity: 0; transform: translateX(-10px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes bagLift {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-3px); }
        }
        @keyframes confettiFall {
          0%   { opacity: 0; transform: translateY(-10px) rotate(0deg); }
          20%  { opacity: 1; }
          100% { opacity: 0; transform: translateY(70px) rotate(220deg); }
        }
        .popIn { animation: popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both; }
        .bell { animation: ringSwing 0.5s ease-in-out 3, ringSwing 2s ease-in-out infinite 1.5s; transform-origin: 100px 42px; }
        .wave { animation: waveOut 1.4s ease-out infinite; }
        .waveL2, .waveR2 { animation-delay: 0.3s; }
        .customer { animation: walkIn 0.6s ease-out both; }
        .bag { animation: bagLift 1.6s ease-in-out infinite 0.8s; transform-origin: 100px 103px; }
        .confetti { animation: confettiFall 1.8s ease-out infinite; }

        @keyframes packReach {
          0%, 100% { transform: rotate(0deg); transform-origin: 105px 96px; }
          50%      { transform: rotate(-14deg); transform-origin: 105px 96px; }
        }
        @keyframes bagFill {
          0%, 100% { transform: scaleY(1); }
          50%      { transform: scaleY(0.96); }
        }
        @keyframes checkDraw {
          0%   { stroke-dasharray: 20; stroke-dashoffset: 20; opacity: 0; }
          40%  { opacity: 1; }
          60%  { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: 0; opacity: 1; }
        }
        .packArm { animation: packReach 1.3s ease-in-out infinite; }
        .groceryBag { animation: bagFill 1.3s ease-in-out infinite; transform-origin: 125px 122px; }
        .checkMark { animation: checkDraw 2.6s ease-in-out infinite; }

        @keyframes sceneFade {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .sceneFade { animation: sceneFade 0.5s ease-out both; }
      `}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    padding: 16,
    transition: 'background 0.5s',
  },
  card: {
    background: '#fff', borderRadius: 24, padding: '40px 32px',
    maxWidth: 380, width: '100%', textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
  },
  tokenBadge: {
    fontSize: 52, fontWeight: 900, color: '#6366f1',
    letterSpacing: 4, lineHeight: 1,
  },
  tokenLabel: { fontSize: 12, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' },
  title: { fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '8px 0 4px' },
  sub: { fontSize: 14, color: '#64748b', lineHeight: 1.5 },
  spinner: {
    width: 56, height: 56, border: '5px solid #e2e8f0',
    borderTop: '5px solid #6366f1', borderRadius: '50%',
    animation: 'spin 1s linear infinite', margin: '12px auto',
  },
  waveBars: { display: 'flex', gap: 4, alignItems: 'center', marginTop: 12 },
  bar: {
    width: 6, height: 10, background: '#6366f1', borderRadius: 3,
    animation: 'wave 1s ease-in-out infinite',
  },
  ringBtn: {
    marginTop: 12, background: '#16a34a', color: '#fff', border: 'none',
    borderRadius: 12, padding: '12px 24px', fontSize: 15, fontWeight: 700,
    cursor: 'pointer',
  },
  infoBox: {
    display: 'flex', justifyContent: 'space-between', width: '100%',
    background: '#f8fafc', borderRadius: 10, padding: '10px 14px',
    marginTop: 4,
  },
  infoLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' },
  infoVal: { fontSize: 13, color: '#1e293b', fontWeight: 700 },
  connRow: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 16 },
  connDot: { width: 8, height: 8, borderRadius: '50%' },
  connText: { fontSize: 11, color: '#94a3b8', fontWeight: 600 },
};
